"use client";

import { DEFAULT_CONFIG, EVAL_MAP_COUNT } from "@/lib/swarm/types";
import { type ParseError } from "@/lib/swarm/dsl-parser";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SwarmLeaderboardTab } from "./SwarmLeaderboardTab";
import { SwarmAdminTab } from "./SwarmAdminTab";

interface SwarmCodePanelProps {
  code: string;
  onChange: (code: string) => void;
  errors: ParseError[];
  activeLine: number | null;
  instrCount: number;
  user: { githubUsername: string } | null;
  onLoadCode: (code: string) => void;
  onTabChange?: (tab: string) => void;
  scoringSubmission?: { id: string; code: string; submittedAt: string } | null;
  challengeEndDate?: string;
}

type Token = { text: string; className: string; title?: string };

const HOVER_DOCS: Record<string, string> = {
  SENSE:
    "SENSE <target> [reg] — Scan 4 adjacent cells for target. Returns direction (1-4), 0 if none. Ties broken randomly.",
  SMELL:
    "SMELL <channel> [reg] — Direction of strongest pheromone gradient (1-4), 0 if none. Ties broken randomly.",
  SNIFF:
    "SNIFF <channel> <dir> [reg] — Raw pheromone intensity (0-255) at cell in direction. HERE = current cell.",
  PROBE:
    "PROBE <dir> [reg] — Cell type at direction: 0=EMPTY 1=WALL 2=FOOD 3=NEST. dir: N E S W HERE RANDOM.",
  CARRYING: "CARRYING [reg] — 1 if ant is holding food, 0 otherwise.",
  ID: "ID [reg] — This ant's index (0-199). Use with MOD for role specialization.",
  SET: "SET <reg> <val> — Set register to value.",
  ADD: "ADD <reg> <val> — reg += val.",
  SUB: "SUB <reg> <val> — reg -= val.",
  MOD: "MOD <reg> <val> — reg %= val (always non-negative).",
  MUL: "MUL <reg> <val> — reg *= val.",
  DIV: "DIV <reg> <val> — reg /= val (integer division, no-op if zero).",
  AND: "AND <reg> <val> — reg &= val (bitwise AND).",
  OR: "OR <reg> <val> — reg |= val (bitwise OR).",
  XOR: "XOR <reg> <val> — reg ^= val (bitwise XOR).",
  LSHIFT: "LSHIFT <reg> <val> — reg <<= val (left shift).",
  RSHIFT: "RSHIFT <reg> <val> — reg >>= val (arithmetic right shift).",
  RANDOM: "RANDOM <reg> <max> — reg = random int in [0, max).",
  MARK: "MARK <channel> <amount> — Add to pheromone on current cell (additive, capped at 255).",
  JMP: "JMP <label|reg> — Unconditional jump. Accepts a label or register.",
  CALL: "CALL <reg> <label> — Save return address in reg, jump to label.",
  JEQ: "JEQ <a> <b> <label> — Jump if a == b.",
  JNE: "JNE <a> <b> <label> — Jump if a != b.",
  JGT: "JGT <a> <b> <label> — Jump if a > b.",
  JLT: "JLT <a> <b> <label> — Jump if a < b.",
  MOVE: "MOVE <dir> — Move one cell. Ends tick. 0 = stay.",
  PICKUP: "PICKUP — Pick up 1 food from current cell.",
  DROP: "DROP — Drop food. Scores 1 point if at nest.",
  TAG: "TAG <value> — Set ant tag (0-7). Zero cost. Use with .tag to name tags. Visible as heatmap overlay.",
  NOP: "NOP — No operation (costs 1 op).",
  FOOD: "FOOD (2) — Sense target: cells with food.",
  WALL: "WALL (1) — Sense target: impassable cells and map edges.",
  NEST: "NEST (3) — Sense target: home base (3×3 area).",
  ANT: "ANT (4) — Sense target: cells occupied by other ants.",
  EMPTY: "EMPTY (0) — Sense target: any passable cell (not wall).",
  N: "N (1) — North (up).",
  E: "E (2) — East (right).",
  S: "S (3) — South (down).",
  W: "W (4) — West (left).",
  NORTH: "NORTH (1) — North (up).",
  EAST: "EAST (2) — East (right).",
  SOUTH: "SOUTH (3) — South (down).",
  WEST: "WEST (4) — West (left).",
  _RANDOM_DIR: "RANDOM (6) — Random cardinal direction each time.",
  HERE: "HERE (5) — Current cell (no movement).",
  CH_RED: "CH_RED (0) — Pheromone channel 0. Shown as red in viewer.",
  CH_BLUE: "CH_BLUE (1) — Pheromone channel 1. Shown as blue in viewer.",
  CH_GREEN: "CH_GREEN (2) — Pheromone channel 2. Shown as green in viewer.",
  CH_YELLOW: "CH_YELLOW (3) — Pheromone channel 3. Shown as yellow in viewer.",
  ".ALIAS":
    ".alias <name> <register> — Name a register. Use the name anywhere a register is expected.",
  ".CONST":
    ".const <NAME> <value> — Define a constant. Substituted into instructions at parse time.",
  ".TAG":
    ".tag <n> <name> — Name a tag value (0-7) for display in viewer toggles and ant info.",
};

function tokenizeLine(line: string): Token[] {
  const tokens: Token[] = [];
  const trimmed = line.trimStart();
  const indent = line.length - trimmed.length;

  if (indent > 0) {
    tokens.push({ text: line.slice(0, indent), className: "" });
  }

  if (!trimmed) return tokens;

  const commentIdx = trimmed.indexOf(";");
  if (commentIdx === 0) {
    tokens.push({ text: trimmed, className: "text-dim italic" });
    return tokens;
  }

  const beforeComment =
    commentIdx >= 0 ? trimmed.slice(0, commentIdx) : trimmed;
  const comment = commentIdx >= 0 ? trimmed.slice(commentIdx) : "";

  if (/^\w+:\s*$/.test(beforeComment)) {
    tokens.push({ text: beforeComment, className: "text-amber font-bold" });
    if (comment) tokens.push({ text: comment, className: "text-dim italic" });
    return tokens;
  }

  const parts = beforeComment.split(/(\s+)/);
  const OPCODES = new Set([
    "SENSE",
    "SMELL",
    "SNIFF",
    "PROBE",
    "CARRYING",
    "ID",
    "SET",
    "ADD",
    "SUB",
    "MOD",
    "MUL",
    "DIV",
    "AND",
    "OR",
    "XOR",
    "LSHIFT",
    "RSHIFT",
    "RANDOM",
    "MARK",
    "JMP",
    "CALL",
    "JEQ",
    "JNE",
    "JGT",
    "JLT",
    "MOVE",
    "PICKUP",
    "DROP",
    "NOP",
    "TAG",
  ]);
  const DIRS = new Set([
    "N",
    "E",
    "S",
    "W",
    "NORTH",
    "EAST",
    "SOUTH",
    "WEST",
    "HERE",
    "RANDOM",
  ]);
  const TARGETS = new Set(["FOOD", "WALL", "NEST", "ANT", "EMPTY"]);
  const CHANNELS = new Set(["CH_RED", "CH_BLUE", "CH_GREEN", "CH_YELLOW"]);
  const REGS = new Set(["r0", "r1", "r2", "r3", "r4", "r5", "r6", "r7"]);
  const DIRECTIVES = new Set([".ALIAS", ".CONST", ".TAG"]);

  let isFirstWord = true;
  for (const part of parts) {
    if (/^\s+$/.test(part)) {
      tokens.push({ text: part, className: "" });
      continue;
    }
    const upper = part.toUpperCase();
    const stripped = upper.replace(/:$/, "");
    const title = HOVER_DOCS[upper] || HOVER_DOCS[stripped];
    if (isFirstWord && DIRECTIVES.has(upper)) {
      tokens.push({
        text: part,
        className: "text-cyan",
        title: HOVER_DOCS[upper],
      });
    } else if (isFirstWord && OPCODES.has(upper)) {
      tokens.push({ text: part, className: "text-cyan font-bold", title });
    } else if (CHANNELS.has(upper)) {
      tokens.push({ text: part, className: "text-purple", title });
    } else if (DIRS.has(upper) || TARGETS.has(upper)) {
      const dirTitle =
        upper === "RANDOM"
          ? HOVER_DOCS["_RANDOM_DIR"]
          : upper === "HERE"
            ? HOVER_DOCS["HERE"]
            : title;
      tokens.push({ text: part, className: "text-green", title: dirTitle });
    } else if (REGS.has(part)) {
      tokens.push({ text: part, className: "text-amber" });
    } else if (/^-?(\d+|0x[\da-f]+)$/i.test(part)) {
      tokens.push({ text: part, className: "text-purple" });
    } else {
      tokens.push({ text: part, className: "text-secondary" });
    }
    isFirstWord = false;
  }

  if (comment) {
    tokens.push({ text: comment, className: "text-dim italic" });
  }

  return tokens;
}

const { mapWidth, mapHeight, antCount } = DEFAULT_CONFIG;

const REFERENCE = `SWARM — Ant Colony Optimization Challenge

  ${antCount} ants share one program on a ${mapWidth}x${mapHeight} grid.
  Find food, bring it to the nest. Communicate
  only through pheromone trails. Score across ${EVAL_MAP_COUNT}
  maps. Hit play to see the default code run,
  then improve it.

─────────────────────────────────────────────

QUICK START

  SENSE FOOD r1       ; scan 4 adjacent cells for food
  MOVE r1             ; walk toward it
  PICKUP              ; grab one unit
  SENSE NEST r1       ; find the way home
  MOVE r1             ; walk toward nest
  DROP                ; deliver (scores if at nest)

─────────────────────────────────────────────

LANGUAGE

  Antssembly is like assembly. The program
  counter persists across ticks — your ant's
  "brain" is  a continuous loop, not restarted
  each tick.

  DIRECTIVES

    .alias dir r1         ; name a register
    .alias trail r2
    .const FOOD CH_RED    ; named constant
    .tag 0 forager        ; name a tag (see TAGS)
    .tag 1 scout

  .tag names double as constants, so you can
  write  TAG forager  instead of  TAG 0.

  LABELS

    search:
      SENSE FOOD dir
      JEQ dir 0 wander
      MOVE dir
      PICKUP
      JMP search
    wander:
      MOVE RANDOM

  ENTRYPOINT

    If a  main:  label exists and isn't at the
    top, a  JMP main  is auto-prepended.

  REGISTERS   r0-r7, 8 general purpose.
              Signed 32-bit integers, init to 0.
              Overflow wraps (32-bit signed).
  COMMENTS    ; everything after semicolon.

─────────────────────────────────────────────

SENSING

  All sensing ops take an optional dest register
  (defaults to r0 if omitted).

  SENSE <target> [reg]  scan 4 adjacent cells
    targets: EMPTY=0  WALL=1  FOOD=2  NEST=3
             ANT=4  (or register)
    returns direction (N=1 E=2 S=3 W=4), 0=none
    ties broken randomly

  SMELL <ch> [reg]      strongest pheromone dir
    ties broken randomly
  SNIFF <ch> <dir> [reg]  intensity 0-255
    dir: HERE N E S W RANDOM (or register)
  PROBE <dir> [reg]     cell type at direction
    returns 0=EMPTY 1=WALL 2=FOOD 3=NEST
  CARRYING [reg]        1 if holding food
  ID [reg]              ant index (0-199)

─────────────────────────────────────────────

ACTIONS — end the ant's tick

  MOVE <dir>    N/E/S/W, RANDOM, or register
  PICKUP        pick up 1 food from current cell
  DROP          drop food (scores if at nest)

  Each ant carries at most 1 food. Multiple ants
  can share a cell (no collisions). Each tick runs
  until an action or the 64-op limit. Then the next
  ant goes. After all 200 ants act, pheromones decay.

─────────────────────────────────────────────

PHEROMONES — 4 channels, intensity 0-255

  CH_RED  CH_BLUE  CH_GREEN  CH_YELLOW
  Toggle R/B/G/Y in the viewer to see them.

  MARK <ch> <amount>  add to pheromone on this cell
                      (additive, capped at 255)

  Trail pattern:
    MARK CH_RED 100     ; add to breadcrumb
    SMELL CH_RED r2     ; follow gradient
    MOVE r2

─────────────────────────────────────────────

ARITHMETIC

  SET r1 5          ADD r1 1          SUB r1 1
  MOD r1 4          MUL r1 3          DIV r1 2
  AND r1 0xFF       OR r1 1           XOR r1 r2
  LSHIFT r1 4       RSHIFT r1 4       RANDOM r1 4
  Second operand can be a register or literal.

  DIV truncates toward zero. MOD is always
  non-negative. Both are no-ops if divisor=0.
  SHR is arithmetic (sign-preserving).
  RANDOM r1 N sets r1 to a value in [0, N).

─────────────────────────────────────────────

CONTROL FLOW

  JMP <label>               unconditional
  JMP <reg>                 indirect (jump to address in reg)
  CALL <reg> <label>        save return addr in reg, jump
  JEQ <a> <b> <label>       jump if a == b
  JNE <a> <b> <label>       jump if a != b
  JGT <a> <b> <label>       jump if a > b
  JLT <a> <b> <label>       jump if a < b

  Function call pattern:
    CALL r7 my_func       ; save return addr, jump
    ; ...returns here...
    my_func:
      ; do work
      JMP r7              ; return to caller

─────────────────────────────────────────────

TAGS — role heatmaps for debugging

  TAG <value>       set ant tag 0-7 (ZERO COST)

  .tag 0 forager    name tags for viewer toggles
  .tag 1 scout      then use:  TAG forager

  Toggle tag heatmaps in the viewer to see
  where each role's ants have walked. Useful
  for visualizing role specialization:

    ID r3
    MOD r3 4
    TAG r3            ; 4 roles, 4 heatmaps

─────────────────────────────────────────────

THE GRID

  EMPTY  passable       WALL   impassable
  FOOD   1-8 units      NEST   deliver here

  Directions: N E S W (or NORTH EAST SOUTH WEST)
  RANDOM = random cardinal direction each call

─────────────────────────────────────────────

MAP TYPES — 12 procedural generators

  Your score is averaged across ${EVAL_MAP_COUNT} maps drawn
  from these types, seeded deterministically.

  open      No internal walls. Random food
            clusters scattered around the nest.

  maze      Wide-corridor maze. 2-wide passages,
            2-wide walls.

  spiral    Concentric wavy ring walls with wide
            random gaps.

  field     Nearly open, a few lazy curvy walls.

  bridge    A vertical wall splits the map with
            2-4 narrow crossings. All food is
            on the far side from the nest.

  gauntlet  Nest far left, food far right.
            Staggered vertical walls with gaps.

  pockets   Circular walled cells with narrow
            entrances and food inside.

  fortress  Nest cornered behind wavy concentric
            walls with gates. Food is deep
            inside the fortress.

  islands   Rooms separated by walls with one
            doorway between adjacent rooms.

  chambers  Rooms carved in rock connected by
            narrow corridors.

  prairie   Food everywhere at varying density,
            no blobs.

  brush     Dense random wall clutter throughout.
            Food in medium clusters.

─────────────────────────────────────────────

SCORING

  Each map: food delivered / food available.
  Score = average ratio across ${EVAL_MAP_COUNT} maps * 1000.
  Deterministic — same code, same score.

─────────────────────────────────────────────

TIPS

  1. Read the default code — it's a complete
     two-trail forager with comments.
  2. SENSE/SMELL return directions — pass to MOVE
  3. MARK when carrying, SMELL when searching
  4. ID + MOD for role specialization
  5. SNIFF reads exact intensity for smarter
     routing (e.g. pick strongest neighbor)
  6. .alias and .tag cost nothing — use them`;

function getTokenAtColumn(tokens: Token[], col: number): Token | null {
  let pos = 0;
  for (const t of tokens) {
    const end = pos + t.text.length;
    if (col >= pos && col < end) return t;
    pos = end;
  }
  return null;
}

export function SwarmCodePanel({
  code,
  onChange,
  errors,
  activeLine,
  instrCount,
  user,
  onLoadCode,
  onTabChange,
  scoringSubmission,
  challengeEndDate,
  jumpToLineRef,
}: SwarmCodePanelProps & { jumpToLineRef?: React.MutableRefObject<((line: number) => void) | null> }) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const highlightRef = useRef<HTMLDivElement>(null);
  const editorWrapRef = useRef<HTMLDivElement>(null);
  const charWidthRef = useRef(0);
  type TabName = "intro" | "code" | "reference" | "leaderboard" | "admin";
  const isDev = process.env.NODE_ENV === 'development';
  const [tab, setTab] = useState<TabName>(() => {
    if (typeof window === "undefined") return "intro";
    const saved = localStorage.getItem("swarm-active-tab") as TabName | null;
    if (saved) return saved;
    if (localStorage.getItem("swarm-seen-reference")) return "code";
    return "intro";
  });

  const switchTab = useCallback(
    (next: TabName) => {
      if (next !== "intro") localStorage.setItem("swarm-seen-reference", "1");
      localStorage.setItem("swarm-active-tab", next);
      setTab(next);
      onTabChange?.(next);
    },
    [onTabChange],
  );
  const [showErrors, setShowErrors] = useState(false);
  const errorsRef = useRef<HTMLDivElement>(null);
  const errorLines = useMemo(() => new Set(errors.map(e => e.line)), [errors]);
  const errorByLine = useMemo(() => new Map(errors.map(e => [e.line, e.message])), [errors]);
  useEffect(() => {
    if (errors.length === 0) setShowErrors(false);
    // Clear stale tooltip when errors change — next mouse move will recompute
    hoverTokenRef.current = null;
    setTooltip(null);
  }, [errors]);
  useEffect(() => {
    if (!showErrors) return;
    const handler = (e: MouseEvent) => {
      if (errorsRef.current && !errorsRef.current.contains(e.target as Node)) {
        setShowErrors(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showErrors]);
  const jumpToLine = useCallback((line: number) => {
    setShowErrors(false);
    switchTab('code');
    requestAnimationFrame(() => {
      const ta = textareaRef.current;
      if (!ta) return;
      const codeLines = ta.value.split('\n');
      let pos = 0;
      for (let i = 0; i < line - 1 && i < codeLines.length; i++) pos += codeLines[i].length + 1;
      ta.focus();
      ta.setSelectionRange(pos, pos + (codeLines[line - 1]?.length ?? 0));
      // Scroll the parent overflow container
      const scrollParent = ta.closest('.overflow-auto');
      if (scrollParent) {
        const lineHeight = 13 * 1.7; // text-[13px] leading-[1.7]
        scrollParent.scrollTop = Math.max(0, (line - 5) * lineHeight);
      }
    });
  }, [switchTab]);
  useEffect(() => {
    if (jumpToLineRef) jumpToLineRef.current = jumpToLine;
  }, [jumpToLineRef, jumpToLine]);
  const [copiedText, setCopiedText] = useState<string | null>(null);
  const currentText =
    tab === "code" ? code : tab === "reference" ? REFERENCE : "";
  const copied = copiedText !== null && copiedText === currentText;
  const [tooltip, setTooltip] = useState<{
    text: string;
    x: number;
    y: number;
    isError?: boolean;
  } | null>(null);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hoverTokenRef = useRef<string | null>(null);

  useEffect(() => {
    if (tab === "code") {
      const ta = textareaRef.current;
      if (!ta) return;
      ta.focus();
      const saved = sessionStorage.getItem("swarm-cursor-pos");
      if (saved) {
        const pos = parseInt(saved, 10);
        ta.setSelectionRange(pos, pos);
        const lineHeight = 13 * 1.7;
        const line = code.substring(0, pos).split("\n").length;
        const scrollParent = ta.closest(".overflow-auto");
        if (scrollParent) scrollParent.scrollTop = Math.max(0, (line - 5) * lineHeight);
      }
    }
  }, [tab]);

  useEffect(() => {
    return () => {
      if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    };
  }, []);

  useEffect(() => {
    const measure = () => {
      const span = document.createElement("span");
      span.style.cssText =
        "font-family:JetBrains Mono,monospace;font-size:13px;position:absolute;visibility:hidden;white-space:pre";
      span.textContent = "X";
      document.body.appendChild(span);
      charWidthRef.current = span.getBoundingClientRect().width;
      document.body.removeChild(span);
    };
    measure();
    document.fonts.ready.then(measure);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Tab") {
        e.preventDefault();
        const ta = textareaRef.current;
        if (!ta) return;
        const start = ta.selectionStart;
        const end = ta.selectionEnd;
        const newVal = code.substring(0, start) + "  " + code.substring(end);
        onChange(newVal);
        requestAnimationFrame(() => {
          ta.selectionStart = ta.selectionEnd = start + 2;
        });
      }
    },
    [code, onChange],
  );

  const handleCopy = useCallback(() => {
    const text = tab === "code" ? code : tab === "reference" ? REFERENCE : "";
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      setCopiedText(text);
      setTimeout(() => setCopiedText(null), 1500);
    });
  }, [code, tab]);

  const userDocs = useMemo(() => {
    const docs = new Map<string, string>();
    const codeLines = code.split("\n");
    for (let i = 0; i < codeLines.length; i++) {
      const raw = codeLines[i];
      const commentIdx = raw.indexOf(";");
      const inlineComment =
        commentIdx >= 0 ? raw.slice(commentIdx + 1).trim() : "";
      const trimmed = (commentIdx >= 0 ? raw.slice(0, commentIdx) : raw).trim();
      const directive = trimmed.startsWith(".alias")
        ? ".alias"
        : trimmed.startsWith(".const")
          ? ".const"
          : trimmed.startsWith(".tag")
            ? ".tag"
            : null;
      if (!directive) continue;
      const parts = trimmed.split(/\s+/);
      if (parts.length < 3) continue;
      const comments: string[] = [];
      if (inlineComment) {
        comments.push(inlineComment);
      }
      for (let j = i - 1; j >= 0; j--) {
        const cl = codeLines[j].trim();
        if (cl.startsWith(";")) {
          comments.unshift(cl.slice(1).trim());
        } else break;
      }
      if (directive === ".tag") {
        const tagName = parts[2];
        const base = `.tag ${parts[1]} ${parts.slice(2).join(" ")}`;
        const desc =
          comments.length > 0 ? `${base} — ${comments.join(" ")}` : base;
        docs.set(tagName.toUpperCase(), desc);
      } else {
        const name = parts[1];
        const base = `${directive} ${name} ${parts[2]}`;
        const desc =
          comments.length > 0 ? `${base} — ${comments.join(" ")}` : base;
        docs.set(name.toUpperCase(), desc);
      }
    }
    return docs;
  }, [code]);

  const highlighted = useMemo(() => {
    return code.split("\n").map((line, i) => ({
      key: i,
      tokens: tokenizeLine(line),
    }));
  }, [code]);

  const handleEditorMouseMove = useCallback(
    (e: React.MouseEvent) => {
      const wrap = editorWrapRef.current;
      if (!wrap || !charWidthRef.current) {
        if (hoverTimerRef.current) {
          clearTimeout(hoverTimerRef.current);
          hoverTimerRef.current = null;
        }
        hoverTokenRef.current = null;
        setTooltip(null);
        return;
      }
      const rect = wrap.getBoundingClientRect();
      const highlightEl = highlightRef.current;
      const lineHeight =
        highlightEl && highlightEl.children.length >= 2
          ? highlightEl.children[1].getBoundingClientRect().top -
            highlightEl.children[0].getBoundingClientRect().top
          : 13 * 1.7;
      const padding = 8;
      const y = e.clientY - rect.top - padding;
      const x = e.clientX - rect.left - padding;
      const lineIdx = Math.floor(y / lineHeight);
      const col = Math.floor(x / charWidthRef.current);
      if (lineIdx < 0 || lineIdx >= highlighted.length || col < 0) {
        if (hoverTimerRef.current) {
          clearTimeout(hoverTimerRef.current);
          hoverTimerRef.current = null;
        }
        hoverTokenRef.current = null;
        setTooltip(null);
        return;
      }
      const token = getTokenAtColumn(highlighted[lineIdx].tokens, col);
      const lineNum = lineIdx + 1;
      const errorMsg = errorByLine.get(lineNum);
      const key = errorMsg ? `err:${lineIdx}:${errorMsg}` : token ? `${lineIdx}:${token.text}` : null;
      if (key === hoverTokenRef.current) return;
      hoverTokenRef.current = key;
      if (hoverTimerRef.current) {
        clearTimeout(hoverTimerRef.current);
        hoverTimerRef.current = null;
      }
      const docText =
        errorMsg ?? token?.title ?? userDocs.get(token?.text.toUpperCase() ?? "");
      if (!docText) {
        setTooltip(null);
        return;
      }
      const lineEl = highlightEl?.children[lineIdx];
      const lineRect = lineEl?.getBoundingClientRect();
      const tokenTop = lineRect
        ? lineRect.top
        : rect.top + padding + lineIdx * lineHeight;
      const tokenLeft =
        (lineRect ? lineRect.left : rect.left + padding) +
        col * charWidthRef.current;
      hoverTimerRef.current = setTimeout(() => {
        setTooltip({ text: docText, x: tokenLeft, y: tokenTop - 4, isError: !!errorMsg });
        hoverTimerRef.current = null;
      }, 150);
    },
    [highlighted, userDocs, errorByLine],
  );

  const handleEditorMouseLeave = useCallback(() => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    hoverTokenRef.current = null;
    setTooltip(null);
  }, []);

  const lines = code.split("\n");

  return (
    <div className="h-full flex flex-col border-r border-stroke min-h-0">
      <div className="flex items-center justify-between px-4 py-2 bg-panel-header border-b border-stroke text-[11px] tracking-wider shrink-0">
        <div className="flex items-center gap-1">
          <button
            onClick={() => switchTab("intro")}
            className={`px-2.5 py-0.5 rounded transition-colors cursor-pointer ${
              tab === "intro"
                ? "text-cyan font-bold bg-elevated"
                : "text-secondary hover:text-primary"
            }`}
          >
            intro
          </button>
          <button
            onClick={() => switchTab("code")}
            className={`px-2.5 py-0.5 rounded transition-colors cursor-pointer ${
              tab === "code"
                ? "text-cyan font-bold bg-elevated"
                : "text-secondary hover:text-primary"
            }`}
          >
            brain.ant
          </button>
          <button
            onClick={() => switchTab("reference")}
            className={`px-2.5 py-0.5 rounded transition-colors cursor-pointer ${
              tab === "reference"
                ? "text-cyan font-bold bg-elevated"
                : "text-secondary hover:text-primary"
            }`}
          >
            reference
          </button>
          <button
            onClick={() => switchTab("leaderboard")}
            className={`px-2.5 py-0.5 rounded transition-colors cursor-pointer ${
              tab === "leaderboard"
                ? "text-cyan font-bold bg-elevated"
                : "text-secondary hover:text-primary"
            }`}
          >
            leaderboard
          </button>
          {isDev && (
            <button
              onClick={() => switchTab("admin")}
              className={`px-2.5 py-0.5 rounded transition-colors cursor-pointer ${
                tab === "admin"
                  ? "text-cyan font-bold bg-elevated"
                  : "text-secondary hover:text-primary"
              }`}
            >
              admin
            </button>
          )}
        </div>
        {(tab === "code" || tab === "reference") && (
          <div className="relative flex items-center gap-3 min-w-0 overflow-visible">
            <button
              onClick={handleCopy}
              className="text-dim hover:text-primary cursor-pointer text-[10px] px-1.5 py-0.5 rounded border border-stroke/50 hover:border-stroke transition-colors shrink-0"
              title="Copy to clipboard"
            >
              {copied ? "copied!" : "copy"}
            </button>
            {errors.length > 0 ? (
              <button
                className="text-red text-[10px] cursor-pointer hover:text-red/80 shrink-0"
                onClick={() => setShowErrors(prev => !prev)}
              >
                {errors.length} {errors.length === 1 ? 'error' : 'errors'}
              </button>
            ) : (
              <span className="text-green">{instrCount} instructions</span>
            )}
            {showErrors && errors.length > 0 && (
              <div ref={errorsRef} className="absolute top-full right-0 mt-1 z-50 bg-panel border border-stroke rounded shadow-lg min-w-[300px] max-w-[450px] max-h-[300px] overflow-y-auto">
                <div className="flex items-center justify-between px-3 py-1.5 border-b border-stroke">
                  <span className="text-red text-[10px] font-bold">{errors.length} {errors.length === 1 ? 'error' : 'errors'}</span>
                  <button
                    className="text-dim hover:text-primary text-[10px] cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigator.clipboard.writeText(errors.map(e => e.message).join('\n'));
                    }}
                  >
                    copy all
                  </button>
                </div>
                {errors.map((err, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-2 px-3 py-1.5 text-[11px] border-b border-stroke/50 last:border-0 hover:bg-elevated/50 group"
                  >
                    <span className="text-red flex-1 min-w-0">{err.message}</span>
                    <div className="flex items-center gap-1.5 shrink-0 opacity-0 group-hover:opacity-100">
                      <button className="text-dim hover:text-primary cursor-pointer text-[9px]" onClick={() => jumpToLine(err.line)}>go</button>
                      <button className="text-dim hover:text-primary cursor-pointer text-[9px]" onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(err.message); }}>copy</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {tab === "intro" ? (
        <div className="flex-1 overflow-y-auto p-5 text-[12px] leading-[1.8] min-h-0">
          <div className="max-w-130">
            <h2 className="text-cyan font-bold text-[14px] mb-3">
              Welcome to SWARM
            </h2>
            <p className="text-secondary mb-4">
              Write a single program &mdash; the ant&rsquo;s{" "}
              <span className="text-amber">&ldquo;brain&rdquo;</span> &mdash;
              that runs identically on{" "}
              <span className="font-bold text-primary">{antCount} ants</span>.
              They explore a grid, pick up food, and bring it to the nest. Ants
              can only communicate through{" "}
              <span className="text-amber">pheromone trails</span> left on the
              grid. Your score (0&ndash;1000) is the average food collection
              ratio across {EVAL_MAP_COUNT} maps.
            </p>

            <h3 className="text-cyan font-bold text-[13px] mb-2">
              Workspace Layout
            </h3>
            <p className="text-secondary mb-1">
              <span className="font-bold text-primary">Left panel</span> &mdash;
              Code editor (<span className="text-amber">brain.ant</span> tab).
              Write your DSL program here. See the{" "}
              <span className="text-amber">reference</span> tab for the full
              instruction set.
            </p>
            <p className="text-secondary mb-4">
              <span className="font-bold text-primary">Right panel</span>{" "}
              &mdash; Simulation viewer. Watch your ants run on practice maps.
              Click <span className="text-amber">Submit</span> to score against
              the {EVAL_MAP_COUNT} evaluation maps.
            </p>

            <h3 className="text-cyan font-bold text-[13px] mb-2">
              Simulation Viewer
            </h3>
            <ul className="text-secondary mb-4 space-y-1 list-none">
              <li>
                &bull; Click a{" "}
                <span className="font-bold text-primary">map thumbnail</span> to
                zoom in and watch that map
              </li>
              <li>
                &bull; Click an{" "}
                <span className="font-bold text-primary">ant</span> in the
                zoomed view to inspect its registers and state
              </li>
              <li>
                &bull; Use <span className="text-amber">Play/Pause</span> and
                speed controls to step through ticks
              </li>
              <li>
                &bull; Toggle <span className="text-amber">R/B/G/Y</span>{" "}
                buttons to visualize pheromone channels
              </li>
              <li>
                &bull; Toggle <span className="text-amber">tag heatmaps</span>{" "}
                to see where each role&rsquo;s ants have been
              </li>
            </ul>

            <h3 className="text-cyan font-bold text-[13px] mb-2">
              Ant Mechanics
            </h3>
            <ul className="text-secondary mb-4 space-y-1 list-none">
              <li>
                &bull; Each ant can carry{" "}
                <span className="font-bold text-primary">1 food</span> at a time
              </li>
              <li>
                &bull; Multiple ants{" "}
                <span className="font-bold text-primary">can share</span> the
                same cell &mdash; no collisions
              </li>
              <li>
                &bull; <span className="text-amber">MOVE</span>,{" "}
                <span className="text-amber">PICKUP</span>, and{" "}
                <span className="text-amber">DROP</span> end the ant&rsquo;s
                tick
              </li>
              <li>
                &bull; Everything else (sensing, math, jumps) is free up to{" "}
                <span className="font-bold text-primary">64 ops/tick</span>
              </li>
              <li>
                &bull; 4 pheromone channels per cell, intensity 0&ndash;255
                (additive MARK), decay &minus;1/tick
              </li>
            </ul>

            <h3 className="text-cyan font-bold text-[13px] mb-2">
              Quick Start
            </h3>
            <pre className="text-secondary bg-surface/50 border border-stroke rounded p-3 mb-4 text-[11px] leading-[1.8]">{`SENSE FOOD r1       ; scan 4 adjacent cells for food
MOVE r1             ; walk toward it
PICKUP              ; grab one unit
SENSE NEST r1       ; find the way home
MOVE r1             ; walk toward nest
DROP                ; deliver (scores if at nest)`}</pre>

            <button
              onClick={() => switchTab("code")}
              className="px-4 py-1.5 rounded bg-cyan/10 border border-cyan/30 text-cyan font-bold text-[12px] hover:bg-cyan/20 transition-colors cursor-pointer"
            >
              Open brain.ant &rarr;
            </button>
          </div>
        </div>
      ) : tab === "code" ? (
        <div className="relative flex-1 flex overflow-auto min-h-0">
          <div className="w-10 shrink-0 bg-panel-header/50 text-right text-[13px] leading-[1.7] pr-2 select-none sticky left-0">
            <div className="pt-2">
              {lines.map((_, i) => {
                const ln = i + 1;
                const isError = errorLines.has(ln);
                const isActive = activeLine === ln;
                return (
                  <div
                    key={i}
                    className={
                      isError
                        ? "text-red bg-red/10 rounded-l"
                        : isActive
                          ? "text-cyan"
                          : "text-dim"
                    }
                  >
                    {isActive ? "\u25B6" : ln}
                  </div>
                );
              })}
            </div>
          </div>
          <div
            ref={editorWrapRef}
            className="flex-1 min-w-0"
            style={{ display: "grid" }}
          >
            <div
              ref={highlightRef}
              className="text-[13px] leading-[1.7] p-2 pointer-events-none whitespace-pre"
              aria-hidden
              style={{ gridArea: "1/1" }}
            >
              {highlighted.map(({ key, tokens }) => {
                const ln = key + 1;
                const lineBg =
                  errorLines.has(ln)
                    ? "bg-red/5"
                    : activeLine === ln
                      ? "bg-cyan/5"
                      : "";
                const isErrorLine = errorLines.has(ln);
                return (
                  <div key={key} className={lineBg}>
                    {tokens.length === 0
                      ? "\u00A0"
                      : tokens.map((t, j) => (
                          <span
                            key={j}
                            className={t.className}
                            style={isErrorLine ? { textDecorationLine: 'underline', textDecorationStyle: 'wavy', textDecorationColor: 'var(--color-red, #f44)', textUnderlineOffset: '3px' } : undefined}
                          >
                            {t.text}
                          </span>
                        ))}
                  </div>
                );
              })}
            </div>
            <textarea
              ref={textareaRef}
              value={code}
              onChange={(e) => onChange(e.target.value)}
              onKeyDown={handleKeyDown}
              onKeyUp={(e) => sessionStorage.setItem("swarm-cursor-pos", String((e.target as HTMLTextAreaElement).selectionStart))}
              onClick={(e) => sessionStorage.setItem("swarm-cursor-pos", String((e.target as HTMLTextAreaElement).selectionStart))}
              onMouseMove={handleEditorMouseMove}
              onMouseLeave={handleEditorMouseLeave}
              className="bg-transparent text-transparent text-[13px] leading-[1.7] p-2 outline-none resize-none caret-cyan selection:bg-cyan/20 selection:text-transparent overflow-hidden"
              style={{ gridArea: "1/1" }}
              rows={lines.length}
              spellCheck={false}
              autoComplete="off"
              autoCapitalize="off"
              wrap="off"
            />
            {tooltip && (
              <div
                className={`fixed z-50 px-3 py-1.5 rounded shadow-lg text-[11px] leading-relaxed pointer-events-none max-w-100 ${
                  tooltip.isError
                    ? "bg-panel border border-red/30 text-red"
                    : "bg-elevated border border-stroke text-primary"
                }`}
                style={{
                  left: tooltip.x,
                  top: tooltip.y,
                  transform: "translateY(-100%)",
                }}
              >
                {tooltip.text}
              </div>
            )}
          </div>
        </div>
      ) : tab === "reference" ? (
        <div className="flex-1 overflow-y-auto p-4 text-[12px] leading-[1.8] min-h-0">
          <pre className="text-secondary whitespace-pre-wrap">{REFERENCE}</pre>
        </div>
      ) : tab === "admin" ? (
        <SwarmAdminTab
          onLoadCode={(loadedCode) => {
            onLoadCode(loadedCode);
            switchTab("code");
          }}
        />
      ) : null}
      {/* Always mounted so data prefetches; hidden when not active */}
      <div className={tab === "leaderboard" ? "flex-1 flex flex-col min-h-0" : "hidden"}>
        <SwarmLeaderboardTab
          user={user}
          scoringSubmission={scoringSubmission}
          challengeEndDate={challengeEndDate}
          onLoadCode={(loadedCode) => {
            onLoadCode(loadedCode);
            switchTab("code");
          }}
        />
      </div>
    </div>
  );
}
